import { create } from 'zustand';
import * as Sentry from '@sentry/react';

import type {
  BootStatus,
  BootNotice,
  BootGateId,
  BootHaltKind,
  BootGateError,
  BootGateRecord,
  BootGateStatus,
  BootFailPolicy,
  BootNoticeInput,
  BootHaltPayload,
  BootLateSettlement,
} from './boot-types';

/**
 * Singleton status store for the boot controller.
 *
 * The transition-legality table below is what makes "a settled gate can never
 * re-gate the app" structural rather than conventional: settled states are
 * terminal here, so no late result, watcher race, or controller bug can move
 * a gate backwards. Illegal requests are refused (returning false) and
 * breadcrumbed instead of throwing — the store must never crash boot.
 *
 * NOT registered in src/store/store-registry.ts on purpose: the registry is a
 * debug-viewer concern with heavy MUI imports, and boot modules must stay
 * import-light and mounted before the themed tree.
 */

const LEGAL_TRANSITIONS: Record<BootGateStatus, readonly BootGateStatus[]> = {
  pending: ['running', 'settled-ok'],
  running: ['settled-ok', 'settled-fail-open', 'halted', 'reloading'],
  // halted → halted is the payload-refresh path; a timed-out recovery attempt
  // may fail open (today's guard behavior), a successful one settles.
  halted: ['settled-ok', 'settled-fail-open', 'halted'],
  reloading: [],
  'settled-ok': [],
  'settled-fail-open': [],
};

const BOOT_STATUS_ORDER: Record<BootStatus, number> = {
  idle: 0,
  running: 1,
  'gates-settled': 2,
  complete: 3,
};

const TIMELINE_CAP = 30;
const LATE_SETTLEMENT_CAP = 10;

type BreadcrumbLevel = 'info' | 'warning';

export function bootBreadcrumb(
  message: string,
  level: BreadcrumbLevel = 'info',
  data?: Record<string, unknown>
): void {
  Sentry.addBreadcrumb({ category: 'boot', message, level, data });
}

export type BootStoreState = {
  bootStatus: BootStatus;
  /** Registration order — also the halt-priority order. */
  gateOrder: BootGateId[];
  gates: Record<BootGateId, BootGateRecord>;
  notices: BootNotice[];
  startedAt: number | null;
  gatesSettledAt: number | null;
  completedAt: number | null;
  webFastPath: boolean;
  routerStageAt: number | null;
};

export type BootGatePatch = Partial<
  Pick<BootGateRecord, 'resuming' | 'halt' | 'error' | 'attempt' | 'policyApplied'>
>;

export type BootStoreActions = {
  initGates(
    defs: Array<Pick<BootGateRecord, 'id' | 'displayName'>>,
    options?: { webFastPath?: boolean }
  ): void;
  transitionGate(id: BootGateId, to: BootGateStatus, patch?: BootGatePatch, note?: string): boolean;
  patchGate(id: BootGateId, patch: BootGatePatch, note?: string): void;
  appendLateSettlement(id: BootGateId, entry: BootLateSettlement): void;
  /** Returns false when a notice with the same id was already queued this boot. */
  queueNotice(notice: BootNoticeInput, source: BootNotice['source']): boolean;
  /** Atomically stamps deliveredAt and returns the batch (StrictMode-safe). */
  drainUndeliveredNotices(): BootNotice[];
  setBootStatus(status: BootStatus, at: number): boolean;
  setRouterStageAt(at: number): void;
  reset(): void;
};

export type BootStore = BootStoreState & BootStoreActions;

// Fresh nested objects per reset — a frozen initial-state constant would share
// mutable gate records across resets.
function createInitialBootState(): BootStoreState {
  return {
    bootStatus: 'idle',
    gateOrder: [],
    gates: {},
    notices: [],
    startedAt: null,
    gatesSettledAt: null,
    completedAt: null,
    webFastPath: false,
    routerStageAt: null,
  };
}

function createGateRecord(def: Pick<BootGateRecord, 'id' | 'displayName'>): BootGateRecord {
  return {
    id: def.id,
    displayName: def.displayName,
    status: 'pending',
    startedAt: null,
    settledAt: null,
    durationMs: null,
    attempt: 1,
    resuming: false,
    error: null,
    halt: null,
    policyApplied: null,
    lateSettlements: [],
    timeline: [],
  };
}

function isSettledStatus(status: BootGateStatus): boolean {
  return status === 'settled-ok' || status === 'settled-fail-open';
}

export const useBootStore = create<BootStore>((set, get) => ({
  ...createInitialBootState(),

  initGates: (defs, options) => {
    const gates: Record<BootGateId, BootGateRecord> = {};
    defs.forEach((def) => {
      gates[def.id] = createGateRecord(def);
    });
    set({
      gates,
      gateOrder: defs.map((def) => def.id),
      webFastPath: options?.webFastPath ?? false,
    });
  },

  transitionGate: (id, to, patch, note) => {
    const state = get();
    const record = state.gates[id];
    if (!record) {
      bootBreadcrumb(`gate:${id} transition to ${to} refused - unknown gate`, 'warning');
      return false;
    }
    const from = record.status;
    if (!LEGAL_TRANSITIONS[from].includes(to)) {
      bootBreadcrumb(`gate:${id} illegal transition ${from}->${to} refused`, 'warning', { note });
      return false;
    }

    const at = Date.now();
    const next: BootGateRecord = { ...record, ...patch, status: to };
    if (to === 'running' && next.startedAt === null) {
      next.startedAt = at;
    }
    if (isSettledStatus(to)) {
      if (next.startedAt === null) next.startedAt = at;
      if (next.settledAt === null) {
        next.settledAt = at;
        next.durationMs = at - next.startedAt;
      }
      next.halt = null;
      next.resuming = false;
    }
    next.timeline = [...record.timeline, { at, from, to, note }].slice(-TIMELINE_CAP);
    set({ gates: { ...state.gates, [id]: next } });

    const level: BreadcrumbLevel =
      to === 'halted' || to === 'reloading' || to === 'settled-fail-open' ? 'warning' : 'info';
    bootBreadcrumb(`gate:${id} ${from}->${to}`, level, {
      gateId: id,
      note,
      attempt: next.attempt,
      durationMs: next.durationMs,
    });
    return true;
  },

  patchGate: (id, patch, note) => {
    const state = get();
    const record = state.gates[id];
    if (!record) return;
    set({ gates: { ...state.gates, [id]: { ...record, ...patch } } });
    if (note) {
      bootBreadcrumb(`gate:${id} ${note}`, 'info', { gateId: id, ...patch });
    }
  },

  appendLateSettlement: (id, entry) => {
    const state = get();
    const record = state.gates[id];
    if (!record) return;
    const lateSettlements = [...record.lateSettlements, entry].slice(-LATE_SETTLEMENT_CAP);
    set({ gates: { ...state.gates, [id]: { ...record, lateSettlements } } });
    bootBreadcrumb(`gate:${id} late-${entry.outcome}`, 'info', { gateId: id, ...entry });
  },

  queueNotice: (notice, source) => {
    const state = get();
    if (state.notices.some((existing) => existing.id === notice.id)) {
      bootBreadcrumb(`notice-deduped:${notice.id}`, 'info', { source });
      return false;
    }
    set({
      notices: [...state.notices, { ...notice, source, createdAt: Date.now(), deliveredAt: null }],
    });
    bootBreadcrumb(`notice-queued:${notice.id}`, 'info', { source, variant: notice.variant });
    return true;
  },

  drainUndeliveredNotices: () => {
    const state = get();
    const undelivered = state.notices.filter((notice) => notice.deliveredAt === null);
    if (undelivered.length === 0) return [];
    const at = Date.now();
    set({
      notices: state.notices.map((notice) =>
        notice.deliveredAt === null ? { ...notice, deliveredAt: at } : notice
      ),
    });
    return undelivered.map((notice) => ({ ...notice, deliveredAt: at }));
  },

  setBootStatus: (status, at) => {
    const state = get();
    if (BOOT_STATUS_ORDER[status] <= BOOT_STATUS_ORDER[state.bootStatus]) {
      if (status !== state.bootStatus) {
        bootBreadcrumb(`boot: illegal status ${state.bootStatus}->${status} refused`, 'warning');
      }
      return false;
    }
    set({
      bootStatus: status,
      ...(status === 'running' ? { startedAt: at } : {}),
      ...(status === 'gates-settled' ? { gatesSettledAt: at } : {}),
      ...(status === 'complete' ? { completedAt: at } : {}),
    });
    bootBreadcrumb(`boot:${status}`, 'info', { at });
    return true;
  },

  setRouterStageAt: (at) => {
    if (get().routerStageAt !== null) return;
    set({ routerStageAt: at });
    bootBreadcrumb('boot:router-stage', 'info', { at });
  },

  reset: () => {
    set(createInitialBootState());
  },
}));

// ----------------------------------------------------------------------
// Selector contract consumed by BootGateHost / BootScreen / diagnostics.

export const selectBootStatus = (state: BootStoreState): BootStatus => state.bootStatus;

export const selectBootComplete = (state: BootStoreState): boolean =>
  state.bootStatus === 'complete';

export const selectChildrenMayMount = (state: BootStoreState): boolean =>
  state.bootStatus === 'gates-settled' || state.bootStatus === 'complete';

export const selectAllGatesSettled = (state: BootStoreState): boolean =>
  state.gateOrder.length > 0 &&
  state.gateOrder.every((id) => isSettledStatus(state.gates[id].status));

export type BootActiveHalt = {
  gateId: BootGateId;
  halt: BootHaltPayload;
  haltKind: BootHaltKind;
  resuming: boolean;
};

/** First halted gate in registration order — permissions outranks connectivity. */
export const selectActiveHalt = (state: BootStoreState): BootActiveHalt | null => {
  const haltedId = state.gateOrder.find((id) => state.gates[id].status === 'halted');
  if (!haltedId) return null;
  const record = state.gates[haltedId];
  if (!record.halt) return null;
  return {
    gateId: haltedId,
    halt: record.halt,
    haltKind: record.halt.kind,
    resuming: record.resuming,
  };
};

export const selectIsReloading = (state: BootStoreState): boolean =>
  state.gateOrder.some((id) => state.gates[id].status === 'reloading');

export type BootGateSummary = {
  id: BootGateId;
  displayName: string;
  status: BootGateStatus;
  durationMs: number | null;
  resuming: boolean;
  attempt: number;
  errorMessage: string | null;
  policyApplied: BootFailPolicy | null;
  lastError: BootGateError | null;
};

export const selectGateSummaries = (state: BootStoreState): BootGateSummary[] =>
  state.gateOrder.map((id) => {
    const record = state.gates[id];
    return {
      id: record.id,
      displayName: record.displayName,
      status: record.status,
      durationMs: record.durationMs,
      resuming: record.resuming,
      attempt: record.attempt,
      errorMessage: record.error?.message ?? null,
      policyApplied: record.policyApplied,
      lastError: record.error,
    };
  });

export const selectGateRecords = (state: BootStoreState): BootGateRecord[] =>
  state.gateOrder.map((id) => state.gates[id]);

export const selectUndeliveredNotices = (state: BootStoreState): BootNotice[] =>
  state.notices.filter((notice) => notice.deliveredAt === null);

export const selectBootTimings = (state: BootStoreState) => ({
  startedAt: state.startedAt,
  gatesSettledAt: state.gatesSettledAt,
  completedAt: state.completedAt,
  routerStageAt: state.routerStageAt,
  webFastPath: state.webFastPath,
});
